import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { del, get, patch, post } from 'utils/network';
import { ApiRoute, ErrorMessage, FeatureFlag } from 'config';

import type { AddressErrors, IAddress, ZipInfo } from 'models/Address';
import Address, { State } from 'models/Address';
import useCurrentUser from 'hooks/useCurrentUser';

import { Component as CaretIcon } from 'icons/icon-ui--caret.svg';
import Modal from 'components/shared/Modal';
import Checkbox from 'components/shared/Checkbox';
import ValidableControl from 'components/shared/ValidableControl';
import Banner from 'components/shared/Banner';
import DeliveryInstructionsTooltip from '../tooltips/DeliveryInstructionsTooltip';
import { type INotification, NotificationType } from '../../../models/Notifications';
import useNotifications from '../../../hooks/useNotifications';
import type IApiResponse from '../../../models/IApiResponse';

type Props = {
  initialAddress?: Address | null;
  isPrimaryEditable?: boolean;
  isAccountManagementPage?: boolean;
  onClose: (addr?: Address) => void;
};

enum ComponentLengthType {
  STREET_NUMBER = 'short_name',
  ROUTE = 'long_name',
}

const MAX_ADDRESS2_LENGTH = 30;

// TODO: consolidate duplicate address form components/logic into one (AddressView.tsx)
function AddressModal({
  initialAddress,
  isPrimaryEditable = false,
  onClose,
  isAccountManagementPage = false,
}: Props): ReactElement {
  const { currentUser } = useCurrentUser();

  const getAttr = (attr) => (initialAddress ? initialAddress[attr] : '');
  const [firstName, setFirstName] = useState(getAttr('first_name'));
  const [lastName, setLastName] = useState(getAttr('last_name'));
  const [address1, setAddress1] = useState(getAttr('address'));
  const [address2, setAddress2] = useState(getAttr('address2') || '');
  const [zip, setZip] = useState(getAttr('zip'));
  const [state, setState] = useState(getAttr('state'));
  const [city, setCity] = useState(getAttr('city'));
  const [phone, setPhone] = useState(getAttr('local_phone_number'));
  const [primary, setPrimary] = useState(!!getAttr('primary'));
  const [isSaved, setIsSaved] = useState(!!initialAddress);
  const [addressSaved, setAddressSaved] = useState(false);

  const [deliveryInstructions, setDeliveryInstructions] = useState(
    getAttr('delivery_instructions') || '',
  );

  const addressInputRef = useRef();
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasFedExError, setFedExError] = useState(false);
  const { dispatch } = useNotifications();
  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];

  const showRegionalDisclaimer = currentUser
    .hasFeatureEnabled(FeatureFlag.REGIONAL_DISCLAIMER);

  const isFedExValidationEnabled = currentUser
    .hasFeatureEnabled(FeatureFlag.FEDEX_VALIDATION);

  const hasAccountManagementEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.ACCOUNT_MANAGEMENT,
  );

  // Has the delete notification when address is deleted
  const deleteNotification = () => {
    if (initialAddress != null) {
      dispatch({
        type: 'addNotification',
        notification: {
          id: initialAddress.id,
          notification_type: NotificationType.ADDRESS_CHANGE,
          operation: 'REMOVE',
        } as INotification,
      });
    }
  };

  // Notification for Submitting a value
  const submitNotification = () => {
    if (initialAddress != null) {
      dispatch({
        type: 'addNotification',
        notification: {
          id: initialAddress.id,
          notification_type: NotificationType.ADDRESS_CHANGE,
          operation: 'ADD',
        } as INotification,
      });
    }
  };
  // Deletes the address when the delete button is clicked
  const deleteAddress = async () => {
    if (initialAddress != null) {
      if (!initialAddress) {
        onClose(initialAddress);
      }
      const response = await del<IApiResponse[]>(
        `${ApiRoute.SHIPPING_ADDRESSES}/${initialAddress.id}`,
      );

      if (response.ok) {
        onClose(initialAddress);
        deleteNotification();
      }
    }
  };

  // Makes sure the values are different from the initial values -- if not save changes is disabled

  useEffect(() => {
    (async () => {
      if (zip.length > 4) {
        const response = await get<ZipInfo>(`/zip_codes/${zip.slice(0, 5)}`);

        if (response.ok) {
          const stateCode = response?.parsedBody?.state_code;
          const cityName = response?.parsedBody?.city;

          if (stateCode && cityName) {
            setState(stateCode);
            setCity(cityName);
          }
        }
      }
    })();
  }, [zip]);

  useEffect(() => {
    const inputElement = addressInputRef.current;
    const autoComplete = new google.maps.places.Autocomplete(
      // @ts-expect-error TODO: fix null safety
      (inputElement),
      {
        types: ['geocode'],
        componentRestrictions: { country: 'us' },
      },
    );

    autoComplete.addListener('place_changed', () => {
      const components = autoComplete.getPlace()?.address_components;

      if (components === undefined) return;

      setAddress1([
        components[0][ComponentLengthType.STREET_NUMBER],
        components[1][ComponentLengthType.ROUTE],
      ].join(' '));

      setState('');
      setCity('');
      setZip('');

      for (let i = 0; i < components.length; i++) {
        const addressType = components[i].types[0];
        const addressLengthType = ComponentLengthType[addressType.toUpperCase()] || 'short_name';

        if (addressType !== 'street_number' && addressType !== 'route') {
          const val = components[i][addressLengthType];

          if (addressType === 'administrative_area_level_1') {
            setState(val);
          } else if (addressType === 'locality') {
            setCity(val);
          } else if (addressType === 'postal_code') {
            setZip(val);
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    if (addressSaved) {
      const timer = setTimeout(() => {
        setAddressSaved(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [addressSaved]);

  const onSubmit = async () => {
    const payload = {
      shipping_address: {
        address: address1,
        address2,
        city,
        delivery_instructions: deliveryInstructions,
        first_name: firstName,
        last_name: lastName,
        local_phone_number: phone,
        primary,
        state,
        zip,
        validate_address: isFedExValidationEnabled && !hasFedExError,
      } as IAddress,
    };

    setLoading(true);

    const response = await (
      initialAddress
        ? patch(`${ApiRoute.SHIPPING_ADDRESSES}/${initialAddress.id}`, payload)
        : post(ApiRoute.SHIPPING_ADDRESSES, payload)
    );

    setLoading(false);

    if (response.status === 422) {
      const parsedBody = response.parsedBody as AddressErrors;

      if (isFedExValidationEnabled) {
        if (
          address1 && parsedBody.address
          && Object.keys(parsedBody).length === 1
        ) {
          setFedExError(true);
          parsedBody.base = [ErrorMessage.UNVERIFIED_ADDRESS];
          if (address2) parsedBody.address2 = parsedBody.address;
        } else if (address1) {
          delete parsedBody.address;
        }
      }
      setErrors(parsedBody);
      if ((parsedBody as AddressErrors).base) {
        document.getElementsByClassName('modal')[0].scrollTop = 0;
      }
    } else {
      setIsSaved(true);
      onClose(new Address(response.parsedBody as IAddress));
      setAddressSaved(true);
    }
    submitNotification();
  };

  useEffect(() => {
    if (address2.length <= MAX_ADDRESS2_LENGTH - 1) {
      setErrors((lastErrors) => {
        const newErrors = { ...lastErrors } as AddressErrors;
        delete newErrors.address2;
        return newErrors;
      });
    } else {
      setErrors((lastErrors) => ({
        ...lastErrors,
        address2: [ErrorMessage.CHAR_LIMIT_ADDRESS2],
      }));
    }
  }, [address2]);

  const handleAddress2Change = (value) => {
    setAddress2((lastValue) => {
      if (value.length <= MAX_ADDRESS2_LENGTH) return value;

      return value.length < lastValue.length
        ? value
        : lastValue;
    });
  };

  const hasChanges = useMemo(() => {
    if (!initialAddress) {
      return true;
    }
    return (firstName !== initialAddress.first_name
      || lastName !== initialAddress.last_name
      || address1 !== initialAddress.address
      || address2 !== (initialAddress.address2 || '')
      || zip !== initialAddress.zip
      || state !== initialAddress.state
      || city !== initialAddress.city
      || phone !== initialAddress.local_phone_number || primary !== initialAddress.primary || deliveryInstructions !== (initialAddress.delivery_instructions || ''));
  }, [
    firstName,
    lastName,
    address1,
    address2,
    zip,
    state,
    city,
    phone,
    primary,
    deliveryInstructions,
    initialAddress,
  ]);

  const ModalContent = (
    <>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="First Name"
          htmlFor="add-address-first-name"
          isInvalid={isInvalid('first_name')}
          errorMessage={getErrorMsg('first_name')}
        >
          <input
            id="add-address-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="Last Name"
          htmlFor="add-address-last-name"
          isInvalid={isInvalid('last_name')}
          errorMessage={getErrorMsg('last_name')}
        >
          <input
            id="add-address-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-12">
        <ValidableControl
          label="Street Address"
          htmlFor="add-address-address"
          isInvalid={isInvalid('address')}
          errorMessage={getErrorMsg('address')}
        >
          <input
            id="add-address-address"
            // @ts-expect-error to-do: fix this error
            ref={addressInputRef}
            placeholder="123 Sample Street"
            type="text"
            value={address1}
            onChange={(e) => setAddress1(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-12">
        <ValidableControl
          label={(
            <>
              Apartment/Suite&nbsp;
              <span>(optional)</span>
            </>
          )}
          htmlFor="add-address-address2"
          isInvalid={isInvalid('address2')}
          invalidClassName={
            hasFedExError || address2.length > MAX_ADDRESS2_LENGTH
              ? 'is-invalid'
              : 'has-warning'
          }
          errorMessage={getErrorMsg('address2')}
        >
          <input
            id="add-address-address2"
            placeholder="XYZ Business, Suite 123"
            type="text"
            value={address2}
            onChange={(e) => handleAddress2Change(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-4 col-12">
        <ValidableControl
          label="Zipcode"
          htmlFor="add-address-zip"
          isInvalid={isInvalid('zip')}
          errorMessage={getErrorMsg('zip')}
        >
          <input
            id="add-address-zip"
            placeholder="12345"
            type="text"
            pattern="\d*"
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-8 col-12">
        <ValidableControl
          label="State"
          htmlFor="add-address-state"
          isInvalid={isInvalid('state')}
          errorMessage={getErrorMsg('state')}
        >
          <div className="c-input-group c-input-group--icon-right">
            <select
              id="add-address-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="" label="Select">&nbsp;</option>
              {Object.entries(State)
                .map(([key, val]) => (
                  <option key={key} value={key}>{val}</option>
                ))}
            </select>
            <button
              type="button"
              className="c-link c-input-icon c-input-icon--right"
            >
              <CaretIcon
                className="c-select__caret-icon c-svg-fill-gray-115-color"
                height="12"
                width="12"
              />
            </button>
          </div>
        </ValidableControl>
      </div>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="City"
          htmlFor="add-address-city"
          isInvalid={isInvalid('city')}
          errorMessage={getErrorMsg('city')}
        >
          <input
            id="add-address-city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="Phone Number"
          htmlFor="add-address-phone"
          isInvalid={isInvalid('local_phone_number')}
          errorMessage={getErrorMsg('local_phone_number')}
        >
          <input
            id="add-address-phone"
            placeholder="1234567890"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-12">
        <ValidableControl
          label={(
            <>
              Special Instructions&nbsp;
              <span>
                (optional)
                {' '}
                <DeliveryInstructionsTooltip />
              </span>
            </>
          )}
          htmlFor="add-address-delivery-instructions"
          isInvalid={isInvalid('delivery_instructions')}
          errorMessage={getErrorMsg('delivery_instructions')}
        >
          <textarea
            id="add-address-delivery-instructions"
            maxLength={60}
            placeholder="Leave package at front door"
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
          />
        </ValidableControl>
      </div>
      {isPrimaryEditable && (
        <div className="col-12 mb-1">
          <div className="c-form-group">
            <Checkbox
              id="add-address-primary"
              label="Make this my Primary Address"
              checked={!!primary}
              // @ts-expect-error to-do:fix error
              disabled={initialAddress && initialAddress.primary}
              onChange={(e) => setPrimary(
                (initialAddress && initialAddress.primary) || e.target.checked,
              )}
            />
          </div>
        </div>
      )}
      {showRegionalDisclaimer && (
        <div className="col-12 u-text--xs mb-2">
          Due to regional availability constraints, certain items may not
          be accessible in your newly selected location. We apologize
          for any inconvenience this may cause, please ensure to review
          your upcoming order.
        </div>
      )}
    </>
  );

  return hasAccountManagementEnabled && isAccountManagementPage ? (
    <div className="c-my-account__address__card">
      {initialAddress?.primary && (
        <h3 className="c-my-account__address__card__heading">
          {initialAddress?.primary ? 'Primary Address' : ''}
        </h3>
      )}
      <div className="row">
        {ModalContent}
        <div
          className={`c-my-account__address__button__row ${!initialAddress?.primary ? '' : 'c-my-account__address__primary__margin__top'}`}
        >
          <button
            onClick={onSubmit}
            type="button"
            className="c-button c-button--primary"
            disabled={!hasChanges}
          >
            Save Changes
          </button>
          {!initialAddress?.primary
            && (
              <button
                onClick={deleteAddress}
                type="button"
                className="c-button c-button--primary"
                disabled={!initialAddress || initialAddress.primary}
              >
                Delete
              </button>
            )}
        </div>
        {addressSaved
          && (
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
            >
              <div>
                <Banner.Success isCentered>
                  <div className="u-text--center">
                    Address updates saved!
                  </div>
                </Banner.Success>
              </div>
            </div>
          )}
      </div>
    </div>
  ) : (
    <Modal
      show
      onClose={() => onClose()}
      header={(
        <Modal.Header className="c-heading--brand-5" center={false}>
          {`${initialAddress ? 'Update' : 'Add'} Shipping Address`}
        </Modal.Header>
      )}
      body={(
        <div className="row">
          {isInvalid('base') && (
            <div className="mb-4 col-12 u-text--sm">
              <div className="c-alert c-alert--error" role="alert">
                <div className="c-alert__text">
                  {getErrorMsg('base')}
                </div>
              </div>
            </div>
          )}
          {ModalContent}
          <div className="col-xs-6 col-12">
            <ValidableControl
              label="First Name"
              htmlFor="add-address-first-name"
              isInvalid={isInvalid('first_name')}
              errorMessage={getErrorMsg('first_name')}
            >
              <input
                id="add-address-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-xs-6 col-12">
            <ValidableControl
              label="Last Name"
              htmlFor="add-address-last-name"
              isInvalid={isInvalid('last_name')}
              errorMessage={getErrorMsg('last_name')}
            >
              <input
                id="add-address-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-12">
            <ValidableControl
              label="Street Address"
              htmlFor="add-address-address"
              isInvalid={isInvalid('address')}
              errorMessage={getErrorMsg('address')}
            >
              <input
                id="add-address-address"
                // @ts-expect-error TODO: fix null safety
                ref={addressInputRef}
                placeholder="123 Sample Street"
                type="text"
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-12">
            <ValidableControl
              label={(
                <>
                  Apartment/Suite&nbsp;
                  <span>(optional)</span>
                </>
              )}
              htmlFor="add-address-address2"
              isInvalid={isInvalid('address2')}
              invalidClassName={
                hasFedExError || address2.length > MAX_ADDRESS2_LENGTH
                  ? 'is-invalid'
                  : 'has-warning'
              }
              errorMessage={getErrorMsg('address2')}
            >
              <input
                id="add-address-address2"
                placeholder="XYZ Business, Suite 123"
                type="text"
                value={address2}
                onChange={(e) => handleAddress2Change(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-xs-4 col-12">
            <ValidableControl
              label="Zipcode"
              htmlFor="add-address-zip"
              isInvalid={isInvalid('zip')}
              errorMessage={getErrorMsg('zip')}
            >
              <input
                id="add-address-zip"
                placeholder="12345"
                type="text"
                pattern="\d*"
                inputMode="numeric"
                maxLength={5}
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-xs-8 col-12">
            <ValidableControl
              label="State"
              htmlFor="add-address-state"
              isInvalid={isInvalid('state')}
              errorMessage={getErrorMsg('state')}
            >
              <div className="c-input-group c-input-group--icon-right">
                <select
                  id="add-address-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  <option value="" label="Select">&nbsp;</option>
                  {Object.entries(State)
                    .map(([key, val]) => (
                      <option key={key} value={key}>{val}</option>
                    ))}
                </select>
                <button
                  type="button"
                  className="c-link c-input-icon c-input-icon--right"
                >
                  <CaretIcon
                    className="c-select__caret-icon c-svg-fill-gray-115-color"
                    height="12"
                    width="12"
                  />
                </button>
              </div>
            </ValidableControl>
          </div>
          <div className="col-xs-6 col-12">
            <ValidableControl
              label="City"
              htmlFor="add-address-city"
              isInvalid={isInvalid('city')}
              errorMessage={getErrorMsg('city')}
            >
              <input
                id="add-address-city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-xs-6 col-12">
            <ValidableControl
              label="Phone Number"
              htmlFor="add-address-phone"
              isInvalid={isInvalid('local_phone_number')}
              errorMessage={getErrorMsg('local_phone_number')}
            >
              <input
                id="add-address-phone"
                placeholder="1234567890"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </ValidableControl>
          </div>
          <div className="col-12">
            <ValidableControl
              label={(
                <>
                  Special Instructions&nbsp;
                  <span>
                    (optional)
                    {' '}
                    <DeliveryInstructionsTooltip />
                  </span>
                </>
              )}
              htmlFor="add-address-delivery-instructions"
              isInvalid={isInvalid('delivery_instructions')}
              errorMessage={getErrorMsg('delivery_instructions')}
            >
              <textarea
                id="add-address-delivery-instructions"
                maxLength={60}
                placeholder="Leave package at front door"
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
              />
            </ValidableControl>
          </div>
          {isPrimaryEditable && (
            <div className="col-12">
              <div className="c-form-group">
                <Checkbox
                  id="add-address-primary"
                  label="Make this my Primary Address"
                  checked={!!primary}
                  // @ts-expect-error TODO: fix null safety
                  disabled={initialAddress && initialAddress.primary}
                  onChange={(e) => setPrimary(
                    (initialAddress && initialAddress.primary) || e.target.checked,
                  )}
                />
              </div>
            </div>
          )}
          {showRegionalDisclaimer && (
            <div className="col-12 u-text--xs">
              Due to regional availability constraints, certain items may not
              be accessible in your newly selected location. We apologize
              for any inconvenience this may cause, please ensure to review
              your upcoming order.
            </div>
          )}
        </div>
      )}
      footer={(
        <>
          <button
            type="button"
            className="c-button c-button--primary-link"
            onClick={() => onClose()}
          >
            Cancel
          </button>
          <button
            type="button"
            className="c-button c-button--primary"
            disabled={isLoading || address2.length > MAX_ADDRESS2_LENGTH}
            onClick={() => onSubmit()}
          >
            {isFedExValidationEnabled && hasFedExError ? (
              'Verify Address'
            ) : (
              `${initialAddress ? 'Update' : 'Add'} Address`
            )}
          </button>
        </>
      )}
    />
  );
}

export default AddressModal;
